export const AU_KM = 149_597_870.7;
export const SPEED_OF_LIGHT_M_S = 299_792_458;
export const SPEED_OF_LIGHT_KM_S = SPEED_OF_LIGHT_M_S / 1000;
export const SOLAR_CONSTANT_W_M2 = 1361;
export const STEFAN_BOLTZMANN = 5.670374419e-8;
export const MERCURY_PERIHELION_AU = 0.307499;
export const MERCURY_SEMIMAJOR_AU = 0.387098;
export const MERCURY_APHELION_AU = 0.466697;
export const MERCURY_ORBIT_DAYS = 87.969257;
export const EARTH_SIDEREAL_YEAR_DAYS = 365.25636;
export const MERCURY_CYCLE_SECONDS = MERCURY_ORBIT_DAYS * 86400;
export const HORIZONS_MERCURY_SOLAR_CONSTANTS_W_M2 = {
  perihelion: 14462,
  mean: 9126,
  aphelion: 6278,
};

export function clamp(value, min = 0, max = 100) {
  return Math.min(max, Math.max(min, value));
}

export function round(value, digits = 2) {
  const factor = 10 ** digits;
  return Math.round((value + Number.EPSILON) * factor) / factor;
}

export function relativeErrorPercent(actual, expected) {
  if (expected === 0) throw new Error("expected cannot be zero");
  return Math.abs((actual - expected) / expected) * 100;
}

export function withinRelativeTolerance(actual, expected, tolerancePercent) {
  return relativeErrorPercent(actual, expected) <= tolerancePercent;
}

export function solarFluxFactor(rAu) {
  if (rAu <= 0) throw new Error("rAu must be positive");
  return 1 / rAu ** 2;
}

export function irradianceAtAU(rAu, solarConstant = SOLAR_CONSTANT_W_M2) {
  return solarConstant * solarFluxFactor(rAu);
}

export function thermalEquilibriumTemperature({
  irradianceWm2,
  albedo = 0.12,
  emissivity = 0.9,
  mode = "subsolar",
}) {
  if (irradianceWm2 <= 0) throw new Error("irradianceWm2 must be positive");
  if (emissivity <= 0) throw new Error("emissivity must be positive");
  const divisor = mode === "spherical" ? 4 : 1;
  return (((1 - albedo) * irradianceWm2) / (divisor * emissivity * STEFAN_BOLTZMANN)) ** 0.25;
}

export function lightTimeSeconds(distanceKm) {
  if (distanceKm < 0) throw new Error("distanceKm cannot be negative");
  return distanceKm / SPEED_OF_LIGHT_KM_S;
}

export function roundTripLightTimeSeconds(distanceKm) {
  return lightTimeSeconds(distanceKm) * 2;
}

export function synodicPeriodDays(periodA, periodB) {
  if (periodA <= 0 || periodB <= 0) throw new Error("periods must be positive");
  return 1 / Math.abs(1 / periodA - 1 / periodB);
}

export function freeSpacePathLossDb(distanceKm, frequencyGHz) {
  if (distanceKm <= 0 || frequencyGHz <= 0) {
    throw new Error("distanceKm and frequencyGHz must be positive");
  }
  const distanceM = distanceKm * 1000;
  const frequencyHz = frequencyGHz * 1_000_000_000;
  return 20 * Math.log10((4 * Math.PI * distanceM * frequencyHz) / SPEED_OF_LIGHT_M_S);
}

export function classifyConjunction({
  elongationDeg,
  observerTargetDistanceAu,
  observerSunDistanceAu = 1,
  thresholdDeg = 5,
}) {
  if (elongationDeg >= thresholdDeg) return "clear";
  return observerTargetDistanceAu > observerSunDistanceAu ? "superior" : "inferior";
}

export function mercuryCycleFromTdbSeconds(tdbSecondsPastJ2000) {
  return tdbSecondsPastJ2000 / MERCURY_CYCLE_SECONDS;
}

export function contactScore({
  clearWindowFraction,
  solarInterferencePenalty,
  relayAvailability,
  latencyMinutes,
  dataRateClass,
}) {
  const dataRateScore = { low: 35, medium: 65, high: 90 }[dataRateClass] ?? 50;
  const latencyPenalty = clamp(latencyMinutes / 2, 0, 30);
  return clamp(
    clearWindowFraction * 45 +
      relayAvailability * 25 +
      dataRateScore * 0.25 -
      solarInterferencePenalty * 25 -
      latencyPenalty
  );
}

export function autonomyLoadIndex({
  blackoutDays,
  processDurationDays,
  commandCriticality,
  localFaultRecovery,
}) {
  return clamp(
    blackoutDays * 4 +
      processDurationDays * 2 +
      commandCriticality * 35 -
      localFaultRecovery * 30
  );
}

export function blackoutSafeManufacturingWindow({
  clearWindowDays,
  processDurationDays,
  marginDays,
}) {
  const requiredDays = processDurationDays + marginDays;
  return {
    requiredDays,
    safe: clearWindowDays >= requiredDays,
    slackDays: round(clearWindowDays - requiredDays, 2),
  };
}

export function resourceConfidenceScore({
  sourceQuality,
  measurementDirectness,
  recency,
  independentConfirmation,
  uncertaintyPenalty,
}) {
  return clamp(
    sourceQuality * 28 +
      measurementDirectness * 28 +
      recency * 16 +
      independentConfirmation * 18 -
      uncertaintyPenalty * 20 +
      10
  );
}

export function processBodyFitScore({
  vacuumAdvantage,
  energyAvailability,
  thermalPenalty,
  toolingBurden,
  feedstockMatch,
}) {
  return clamp(
    vacuumAdvantage * 24 +
      energyAvailability * 26 +
      feedstockMatch * 24 -
      thermalPenalty * 18 -
      toolingBurden * 14 +
      20
  );
}

export const defaultScenarioWeights = {
  benefits: {
    energy: 1,
    vacuum: 0.75,
    optionality: 1,
    processFit: 1,
    resourceConfidence: 1,
  },
  risks: {
    transport: 1,
    thermal: 0.9,
    comms: 0.8,
    feedstock: 1,
    processMaturity: 0.8,
  },
};

function weightedAverage(values, weights) {
  let numerator = 0;
  let denominator = 0;
  for (const [key, weight] of Object.entries(weights)) {
    numerator += (values[key] ?? 0) * weight;
    denominator += weight;
  }
  return denominator ? numerator / denominator : 0;
}

export function industrialUtility(scenario, weights = defaultScenarioWeights) {
  const benefitScore = weightedAverage(scenario.benefits, weights.benefits);
  const riskScore = weightedAverage(scenario.risks, weights.risks);
  const uncertaintyPenalty = scenario.uncertaintyPenalty ?? 0;
  const utility = clamp(50 + (benefitScore - riskScore) * 0.62 - uncertaintyPenalty * 0.42);
  return {
    benefitScore: round(benefitScore, 2),
    riskScore: round(riskScore, 2),
    uncertaintyPenalty,
    utility: round(utility, 2),
  };
}

export function rankScenarios(scenarios, weights = defaultScenarioWeights) {
  return scenarios
    .map((scenario) => ({ ...scenario, scores: industrialUtility(scenario, weights) }))
    .sort((a, b) => b.scores.utility - a.scores.utility);
}

export function seededRandom(seedText = "interstellar-feasibility-v1") {
  let state = 0x811c9dc5;
  for (let index = 0; index < seedText.length; index += 1) {
    state ^= seedText.charCodeAt(index);
    state = Math.imul(state, 0x01000193);
  }
  return () => {
    state = (Math.imul(1664525, state >>> 0) + 1013904223) >>> 0;
    return state / 0x100000000;
  };
}

function perturbValue(value, random, span) {
  return clamp(value + (random() * 2 - 1) * span);
}

function percentile(sortedValues, p) {
  if (sortedValues.length === 0) return 0;
  const index = (sortedValues.length - 1) * p;
  const lower = Math.floor(index);
  const upper = Math.ceil(index);
  if (lower === upper) return sortedValues[lower];
  return sortedValues[lower] + (sortedValues[upper] - sortedValues[lower]) * (index - lower);
}

function perturbScenario(scenario, random) {
  const uncertaintyPenalty = scenario.uncertaintyPenalty ?? 0;
  const benefitSpan = 2.5 + uncertaintyPenalty * 0.35;
  const riskSpan = 3 + uncertaintyPenalty * 0.4;
  return {
    ...scenario,
    benefits: Object.fromEntries(
      Object.entries(scenario.benefits).map(([key, value]) => [
        key,
        perturbValue(value, random, benefitSpan),
      ])
    ),
    risks: Object.fromEntries(
      Object.entries(scenario.risks).map(([key, value]) => [
        key,
        perturbValue(value, random, riskSpan),
      ])
    ),
    uncertaintyPenalty: perturbValue(
      uncertaintyPenalty,
      random,
      Math.max(2, uncertaintyPenalty * 0.2)
    ),
  };
}

export function scenarioUncertaintyAnalysis(
  scenarios,
  { weights = defaultScenarioWeights, iterations = 256, seed = "interstellar-feasibility-v1" } = {}
) {
  if (!Number.isInteger(iterations) || iterations < 16) {
    throw new Error("iterations must be an integer >= 16");
  }
  const random = seededRandom(seed);
  const buckets = new Map(
    scenarios.map((scenario) => [
      scenario.id,
      {
        scenarioId: scenario.id,
        name: scenario.name,
        utilities: [],
        benefitScores: [],
        riskScores: [],
        winnerCount: 0,
      },
    ])
  );

  for (let index = 0; index < iterations; index += 1) {
    const perturbed = scenarios.map((scenario) => perturbScenario(scenario, random));
    const ranked = rankScenarios(perturbed, weights);
    const winner = buckets.get(ranked[0].id);
    if (winner) winner.winnerCount += 1;
    for (const scenario of ranked) {
      const bucket = buckets.get(scenario.id);
      if (!bucket) continue;
      bucket.utilities.push(scenario.scores.utility);
      bucket.benefitScores.push(scenario.scores.benefitScore);
      bucket.riskScores.push(scenario.scores.riskScore);
    }
  }

  const summaries = [...buckets.values()]
    .map((bucket) => {
      const utilities = [...bucket.utilities].sort((a, b) => a - b);
      const benefitScores = [...bucket.benefitScores].sort((a, b) => a - b);
      const riskScores = [...bucket.riskScores].sort((a, b) => a - b);
      return {
        scenarioId: bucket.scenarioId,
        name: bucket.name,
        iterations,
        seed,
        utilityP10: round(percentile(utilities, 0.1), 2),
        utilityMedian: round(percentile(utilities, 0.5), 2),
        utilityP90: round(percentile(utilities, 0.9), 2),
        benefitMedian: round(percentile(benefitScores, 0.5), 2),
        riskMedian: round(percentile(riskScores, 0.5), 2),
        winProbability: round((bucket.winnerCount / iterations) * 100, 2),
        downsideSpread: round(percentile(utilities, 0.5) - percentile(utilities, 0.1), 2),
      };
    })
    .sort((a, b) => b.utilityMedian - a.utilityMedian);

  return {
    seed,
    iterations,
    summaries,
    mostLikelyWinner: [...summaries].sort((a, b) => b.winProbability - a.winProbability)[0],
  };
}

export function scenarioBusinessCase(
  scenario,
  {
    capitalUsdBn,
    launchMassT,
    horizonYears,
    weights = defaultScenarioWeights,
    planningLaunchCostUsdPerKg = 1500,
  }
) {
  if (capitalUsdBn <= 0 || launchMassT <= 0 || horizonYears <= 0) {
    throw new Error("capitalUsdBn, launchMassT, and horizonYears must be positive");
  }
  if (planningLaunchCostUsdPerKg <= 0) {
    throw new Error("planningLaunchCostUsdPerKg must be positive");
  }
  const scores = industrialUtility(scenario, weights);
  const destinationDifficulty =
    0.55 + scenario.risks.transport / 100 * 1.8 + (scenario.uncertaintyPenalty ?? 0) / 100;
  const launchCostBn =
    (launchMassT * 1000 * planningLaunchCostUsdPerKg * destinationDifficulty) / 1_000_000_000;
  const developmentCapexBn =
    8 +
    scenario.risks.processMaturity * 0.45 +
    scenario.risks.thermal * 0.22 +
    scenario.risks.comms * 0.15 +
    (scenario.uncertaintyPenalty ?? 0) * 0.35;
  const operationsReserveBn =
    developmentCapexBn * (0.15 + (scenario.uncertaintyPenalty ?? 0) / 200) +
    horizonYears * 0.18 * (scenario.risks.comms / 100);
  const totalProgramNeedBn = round(launchCostBn + developmentCapexBn + operationsReserveBn, 2);
  const capitalGapBn = round(Math.max(0, totalProgramNeedBn - capitalUsdBn), 2);
  const fundingCoverage = clamp((capitalUsdBn / totalProgramNeedBn) * 100);
  const businessReadiness = clamp(
    scores.utility * 0.38 +
      fundingCoverage * 0.32 +
      scenario.benefits.resourceConfidence * 0.18 +
      (100 - scenario.risks.processMaturity) * 0.07 +
      (100 - scenario.risks.feedstock) * 0.05 -
      (scenario.uncertaintyPenalty ?? 0) * 0.22
  );
  const annualCapitalBurnBn = totalProgramNeedBn / horizonYears;
  const capitalEfficiency = scores.utility / Math.max(totalProgramNeedBn, 1);
  const breakEvenPressure = clamp(
    35 +
      annualCapitalBurnBn * 1.9 +
      scenario.risks.feedstock * 0.22 +
      scenario.risks.transport * 0.16 -
      scenario.benefits.optionality * 0.12
  );

  return {
    scenarioId: scenario.id,
    name: scenario.name,
    mode: "planning-heuristic-not-price-quote",
    planningLaunchCostUsdPerKg,
    destinationDifficulty: round(destinationDifficulty, 2),
    launchCostBn: round(launchCostBn, 2),
    developmentCapexBn: round(developmentCapexBn, 2),
    operationsReserveBn: round(operationsReserveBn, 2),
    totalProgramNeedBn,
    capitalGapBn,
    fundingCoverage: round(fundingCoverage, 1),
    annualCapitalBurnBn: round(annualCapitalBurnBn, 2),
    capitalEfficiency: round(capitalEfficiency, 3),
    breakEvenPressure: round(breakEvenPressure, 1),
    businessReadiness: round(businessReadiness, 1),
  };
}

export function scenarioBusinessCases(scenarios, inputs) {
  return scenarios
    .map((scenario) => scenarioBusinessCase(scenario, inputs))
    .sort((a, b) => b.businessReadiness - a.businessReadiness);
}

export function sensitivityAnalysis(scenarios, baseWeights = defaultScenarioWeights) {
  const energyHeavy = structuredClone(baseWeights);
  energyHeavy.benefits.energy = 1.8;
  energyHeavy.risks.transport = 0.75;

  const transportConservative = structuredClone(baseWeights);
  transportConservative.risks.transport = 1.8;
  transportConservative.benefits.energy = 0.75;

  return [
    { id: "base", label: "Balanced", rankings: rankScenarios(scenarios, baseWeights) },
    { id: "energy-heavy", label: "Energy heavy", rankings: rankScenarios(scenarios, energyHeavy) },
    {
      id: "transport-conservative",
      label: "Transport conservative",
      rankings: rankScenarios(scenarios, transportConservative),
    },
  ];
}

export function paretoFrontier(scoredScenarios) {
  return scoredScenarios.filter((candidate) => {
    return !scoredScenarios.some((other) => {
      if (other.id === candidate.id) return false;
      const otherBetterOrEqual =
        other.scores.utility >= candidate.scores.utility &&
        (other.uncertaintyPenalty ?? 0) <= (candidate.uncertaintyPenalty ?? 0);
      const otherStrictlyBetter =
        other.scores.utility > candidate.scores.utility ||
        (other.uncertaintyPenalty ?? 0) < (candidate.uncertaintyPenalty ?? 0);
      return otherBetterOrEqual && otherStrictlyBetter;
    });
  });
}

export function asteroidCandidateScore({
  closeApproachFrequency,
  vInfKms,
  diameterConfidence,
  orbitAccessibility,
  sourceUncertainty,
  resourceClassConfidence,
}) {
  return clamp(
    closeApproachFrequency * 18 +
      diameterConfidence * 18 +
      orbitAccessibility * 24 +
      resourceClassConfidence * 18 -
      vInfKms * 4 -
      sourceUncertainty * 22 +
      20
  );
}

export function sourceQualityScore({
  authority,
  reproducibility,
  licenseClarity,
  uncertaintyDisclosure,
  freshness,
  independence,
}) {
  return clamp(
    authority * 24 +
      reproducibility * 22 +
      licenseClarity * 16 +
      uncertaintyDisclosure * 18 +
      freshness * 10 +
      independence * 10
  );
}

export function modelArtifactReadinessScore({
  sourceTraceability,
  queryReproducibility,
  unitsCoverage,
  uncertaintyCoverage,
  runtimeGenerated,
  independentValidation,
}) {
  return clamp(
    sourceTraceability * 22 +
      queryReproducibility * 18 +
      unitsCoverage * 16 +
      uncertaintyCoverage * 18 +
      runtimeGenerated * 14 +
      independentValidation * 12
  );
}

export function formulaSampleOutput(formulaId, input) {
  switch (formulaId) {
    case "solar-flux":
      return {
        factor: round(solarFluxFactor(input.rAu), 3),
        irradianceWm2: round(irradianceAtAU(input.rAu), 1),
      };
    case "thermal-equilibrium":
      return {
        subsolarK: round(
          thermalEquilibriumTemperature({
            irradianceWm2: input.irradianceWm2,
            albedo: input.albedo,
            emissivity: input.emissivity,
            mode: "subsolar",
          }),
          1
        ),
        sphericalK: round(
          thermalEquilibriumTemperature({
            irradianceWm2: input.irradianceWm2,
            albedo: input.albedo,
            emissivity: input.emissivity,
            mode: "spherical",
          }),
          1
        ),
      };
    case "light-time":
      return {
        oneWaySeconds: round(lightTimeSeconds(input.distanceKm), 2),
        roundTripMinutes: round(roundTripLightTimeSeconds(input.distanceKm) / 60, 2),
      };
    case "synodic-period":
      return {
        days: round(synodicPeriodDays(input.periodA, input.periodB), 2),
      };
    case "free-space-path-loss":
      return {
        fsplDb: round(freeSpacePathLossDb(input.distanceKm, input.frequencyGHz), 2),
      };
    case "resource-confidence":
      return {
        score: round(resourceConfidenceScore(input), 1),
      };
    case "process-body-fit":
      return {
        score: round(processBodyFitScore(input), 1),
      };
    case "autonomy-load":
      return {
        index: round(autonomyLoadIndex(input), 1),
      };
    case "source-quality":
      return {
        score: round(sourceQualityScore(input), 1),
      };
    case "model-artifact-readiness":
      return {
        score: round(modelArtifactReadinessScore(input), 1),
      };
    default:
      return { note: "No executable sample is registered for this formula yet." };
  }
}
